import React, { useState, useContext, useEffect } from "react";
import { i18n } from "../../translate/i18n";
import { 
    Avatar, 
    CardHeader, 
    Grid,
    Dialog,
    DialogContent,
    Chip,
    Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastUtils from "../../utils/toastUtils";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import WarningIcon from "@material-ui/icons/Warning";
import GroupIcon from "@material-ui/icons/Group";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
    // Estilos para o modal da imagem
    imageModal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    imageModalContent: {
        outline: "none",
        maxWidth: "90vw",
        maxHeight: "90vh",
    },
    expandedImage: {
        width: "100%",
        height: "auto",
        maxWidth: "500px",
        borderRadius: theme.spacing(1),
    },
    clickableAvatar: {
        cursor: "pointer",
        "&:hover": {
            opacity: 0.8,
        },
    },
    responseTimer: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        justifyContent: "flex-end",
    },
    timerChip: {
        fontSize: "0.9rem",
        fontWeight: "bold",
        fontFamily: "monospace",
    },
    timerWarning: {
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
        animation: "$blink 1s infinite",
    },
    timerOvertime: {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        animation: "$blink 1s infinite",
    },
    "@keyframes blink": {
        "0%": { opacity: 1 },
        "50%": { opacity: 0.5 },
        "100%": { opacity: 1 },
    },
}));

const TicketInfo = ({ contact, ticket, onClick }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const [imageModalOpen, setImageModalOpen] = useState(false);
    
    // Estados do timer
    const [currentTime, setCurrentTime] = useState(new Date());
    const [hasWarned, setHasWarned] = useState(false);
    const [hasAlerted, setHasAlerted] = useState(false);
    const [timerSettings, setTimerSettings] = useState({
        timeMonitoringEnabled: false,
        responseTimeLimit: 30,
        timeMonitoringWarningMinutes: 25,
    });

    // Função para abrir modal da imagem
    const handleImageClick = (e) => {
        e.stopPropagation(); // Prevenir que o clique no avatar execute outros handlers
        if (contact && contact.urlPicture) {
            setImageModalOpen(true);
        }
    };

    // Função para fechar modal da imagem
    const handleImageModalClose = () => {
        setImageModalOpen(false);
    };

    // Carregar configurações do timer
    useEffect(() => {
        const fetchTimerSettings = async () => {
            try {
                const { data } = await api.get('/companies/settings');
                const settings = data || {};
                setTimerSettings({
                    timeMonitoringEnabled: settings.timeMonitoringEnabled || false,
                    responseTimeLimit: parseInt(settings.responseTimeLimit) || 30,
                    timeMonitoringWarningMinutes: parseInt(settings.timeMonitoringWarningMinutes) || 25,
                });
            } catch (error) {
                console.error('Erro ao carregar configurações do timer:', error);
            }
        };
        fetchTimerSettings();
    }, []);

    // Atualizar o tempo atual a cada segundo
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Calcular informações do timer
    const getTimerInfo = () => {
        if (!ticket || !ticket.id || ticket.status !== 'open' || ticket.userId !== user.id || !timerSettings.timeMonitoringEnabled) {
            return null;
        }

        const ticketOpenTime = new Date(ticket.updatedAt || ticket.createdAt);
        const elapsedMinutes = Math.floor((currentTime.getTime() - ticketOpenTime.getTime()) / (1000 * 60));
        const remainingMinutes = timerSettings.responseTimeLimit - elapsedMinutes;
        
        const isWarning = elapsedMinutes >= timerSettings.timeMonitoringWarningMinutes && remainingMinutes > 0;
        const isOvertime = remainingMinutes <= 0;

        return {
            elapsedMinutes,
            remainingMinutes,
            isWarning,
            isOvertime,
            displayTime: isOvertime ? 
                `+${Math.abs(remainingMinutes)}min` : 
                `${Math.max(0, remainingMinutes)}min`
        };
    };

    // Gerenciar notificações
    useEffect(() => {
        const timerInfo = getTimerInfo();
        if (!timerInfo) {
            setHasWarned(false);
            setHasAlerted(false);
            return;
        }

        // Aviso de aproximação do limite
        if (timerInfo.isWarning && !hasWarned) {
            setHasWarned(true);
            toastUtils.warning(
                "Tempo de Resposta",
                `⚠️ Ticket #${ticket.id} - Tempo de resposta se esgotando! ${timerInfo.remainingMinutes} minutos restantes.`,
                {
                    position: "top-right",
                    autoClose: 10000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                }
            );
        }

        // Alerta de tempo esgotado
        if (timerInfo.isOvertime && !hasAlerted) {
            setHasAlerted(true);
            toastUtils.error(
                "Tempo Esgotado",
                `🚨 Ticket #${ticket.id} - Tempo de resposta esgotado! ${Math.abs(timerInfo.remainingMinutes)} minutos de atraso.`,
                {
                    position: "top-right",
                    autoClose: 15000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                }
            );
        }
    }, [currentTime, ticket, hasWarned, hasAlerted, user.id]);

    // Reset dos estados quando o ticket muda
    useEffect(() => {
        setHasWarned(false);
        setHasAlerted(false);
    }, [ticket && ticket.id]);

    const renderCardReader = () => {
        const acceptedParticipants = (ticket?.participants || []).filter(p => p.status === "accepted");

        return (
            <CardHeader
                onClick={onClick}
                style={{ cursor: "pointer" }}
                titleTypographyProps={{ noWrap: true }}
                subheaderTypographyProps={{ component: "div" }}
                avatar={
                    <Avatar 
                        src={contact && contact.urlPicture} 
                        alt="contact_image" 
                        className={classes.clickableAvatar}
                        onClick={handleImageClick}
                    />
                }
                title={`${(contact && contact.name) || '(sem contato)'} #${ticket && ticket.id}`}
                subheader={
                    <div>
                        <div style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                            {[
                                ticket && ticket.user && `${i18n.t("messagesList.header.assignedTo")} ${ticket.user.name}`,
                                contact && contact.contactWallets && contact.contactWallets.length > 0
                                    ? `• ${i18n.t("wallets.wallet")}: ${(contact.contactWallets[0].wallet && contact.contactWallets[0].wallet.name) || 'N/A'}`
                                    : null
                            ].filter(Boolean).join(' ')}
                        </div>
                        {acceptedParticipants.length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                                <Chip
                                    size="small"
                                    icon={<GroupIcon style={{ fontSize: 16, color: '#1976d2' }} />}
                                    label={`${acceptedParticipants.length === 1 ? 'Co-atendente' : 'Co-atendentes'}: ${acceptedParticipants.map(p => p.user?.name || 'Agente').join(", ")}`}
                                    style={{ 
                                        height: 20, 
                                        fontSize: '0.72rem', 
                                        backgroundColor: 'rgba(25, 118, 210, 0.1)', 
                                        color: '#1565c0',
                                        fontWeight: 500,
                                        maxWidth: '100%'
                                    }}
                                />
                            </div>
                        )}
                    </div>
                }
            />
        );
    }


    const timerInfo = getTimerInfo();

    return (
        <React.Fragment>
            <Grid container alignItems="center" spacing={2}>
                {/* Conteúdo do contato à esquerda */}
                <Grid item xs={timerInfo ? 8 : 12}>
                    {renderCardReader()}
                </Grid>
                
                {/* Timer de resposta à direita */}
                {timerInfo && (
                    <Grid item xs={4} style={{ display: 'flex', alignItems: 'center' }}>
                        <Box className={classes.responseTimer}>
                            <Chip
                                icon={timerInfo.isOvertime ? <WarningIcon /> : <AccessTimeIcon />}
                                label={timerInfo.isOvertime ? 
                                    `${timerInfo.displayTime} atraso` : 
                                    `${timerInfo.displayTime} restante`
                                }
                                className={classes.timerChip}
                                color={
                                    timerInfo.isOvertime 
                                        ? "secondary" 
                                        : timerInfo.isWarning 
                                            ? "primary" 
                                            : "default"
                                }
                                variant={timerInfo.isOvertime || timerInfo.isWarning ? "default" : "outlined"}
                                size="small"
                            />
                        </Box>
                    </Grid>
                )}
            </Grid>

            {/* Modal da Imagem */}
            <Dialog
                open={imageModalOpen}
                onClose={handleImageModalClose}
                className={classes.imageModal}
                maxWidth="md"
                fullWidth
            >
                <DialogContent className={classes.imageModalContent}>
                    <img 
                        src={contact && contact.urlPicture} 
                        alt={(contact && contact.name) || "Foto do contato"}
                        className={classes.expandedImage}
                    />
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
};

export default TicketInfo;